import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, Share2, Instagram } from 'lucide-react';
import Confetti from 'react-confetti';
import html2canvas from 'html2canvas';
import { ThemeToggle, ThemeProvider } from '@/lib/ThemeProvider';
import { ErrorPage } from './ErrorPage';
import { Footer } from '../ui/footer';
import { useAuth } from '@/hooks/useAuth';
import { Button } from "@/components/ui/button";
import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast";
import { CircleCheckIcon, XIcon } from "lucide-react";
// import { RankingPage } from './RankingPage';
import { PointLeadersGraph } from './PointLeadersGraph';

interface RankingData {
  topReaders: {
    position: number;
    streak: number;
  }[];
  userStreak: number;
}

// Tipos para melhorar a tipagem
interface StreakData {
  currentStreak: number;
  maxStreak: number;
  weekdays: {
    domingo: boolean;
    segunda: boolean;
    terca: boolean;
    quarta: boolean;
    quinta: boolean;
    sexta: boolean;
    sabado: boolean;
  };
  url: string;
}

interface ReadingDate {
  date: string;
  postTitle: string;
}

interface ReaderInfo {
  email: string;
  postId: string;
}

// Tipos de erro possíveis
export enum ErrorCode {
  EXPIRED_LINK = 'EXPIRED_LINK',
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_POST = 'INVALID_POST',
  NETWORK_ERROR = 'NETWORK_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  DATABASE_ERROR = 'DATABASE_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  NOT_FOUND = 'NOT_FOUND',
  UNKNOWN = 'UNKNOWN'
}

interface ErrorDetails {
  code: ErrorCode;
  message: string;
  errorId?: string;
  details?: string;
}

// Novo tipo para melhorar a organização
interface SubmitResult {
  success: boolean;
  data?: any;
  error?: ErrorDetails;
}

// Adicione estas constantes no início do arquivo
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 segundo
const ERROR_MESSAGES: Record<ErrorCode, string> = {
  DATABASE_ERROR: "Estamos com uma instabilidade temporária. Por favor, tente novamente em alguns instantes.",
  INVALID_EMAIL: "Por favor, forneça um e-mail válido para continuar.",
  NETWORK_ERROR: "Parece que você está sem conexão. Verifique sua internet e tente novamente.",
  SERVER_ERROR: "Ocorreu um erro no servidor. Nossa equipe foi notificada.",
  EXPIRED_LINK: "Este link expirou. Por favor, solicite um novo link.",
  INVALID_POST: "Post não encontrado ou inválido.",
  UNAUTHORIZED: "Você precisa estar logado para acessar esta funcionalidade.",
  NOT_FOUND: "Recurso não encontrado.",
  UNKNOWN: "Ocorreu um erro inesperado. Por favor, tente novamente."
};

const backendUrl = 'https://temp.testeswaffle.org';

// Função auxiliar para obter dados do usuário
const getUserInfo = (searchParams: URLSearchParams): { email: string | null; postId: string | null } => {
  const storedReaderInfo = localStorage.getItem('reader_info');
  const readerInfo: ReaderInfo | null = storedReaderInfo ? JSON.parse(storedReaderInfo) : null;

  const email = searchParams.get('email') || readerInfo?.email || null;
  const postId = searchParams.get('id') || readerInfo?.postId || null;

  if (email && postId) {
    localStorage.setItem('reader_info', JSON.stringify({ email, postId }));
  }

  return { email, postId };
};

// Função auxiliar para fazer a requisição à API com retry
const submitToApi = async (email: string, postId: string, searchParams: URLSearchParams, retryCount = 0): Promise<SubmitResult> => {
  try {
      const response = await fetch(`${backendUrl}/streaks/read`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        email,
        postId,
        utm_source: searchParams.get('utm_source') || 'undefined',
        utm_medium: searchParams.get('utm_medium') || 'undefined',
        utm_channel: searchParams.get('utm_channel') || 'undefined',
        utm_campaign: searchParams.get('utm_campaign') || 'undefined'
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      
      // Se for erro de banco de dados e ainda não atingiu o limite de retries
      if (response.status === 503 && retryCount < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
        return submitToApi(email, postId, searchParams, retryCount + 1);
      }

      return {
        success: false,
        error: {
          code: response.status === 404 ? ErrorCode.NOT_FOUND : 
                response.status === 503 ? ErrorCode.DATABASE_ERROR :
                ErrorCode.SERVER_ERROR,
          message: errorData.message || ERROR_MESSAGES.SERVER_ERROR
        }
      };
    }

    const data = await response.json();
    return { success: true, data: data.data };
  } catch (error) {
    // Se for erro de rede e ainda não atingiu o limite de retries
    if (error instanceof TypeError && error.message === 'Failed to fetch' && retryCount < MAX_RETRIES) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
      return submitToApi(email, postId, searchParams, retryCount + 1);
    }

    return {
      success: false,
      error: {
        code: ErrorCode.NETWORK_ERROR,
        message: ERROR_MESSAGES.NETWORK_ERROR
      }
    };
  }
};

// Novos componentes
const MonthNavigation = ({
  currentDate,
  onPrevMonth,
  onNextMonth
}: {
  currentDate: Date;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}) => (
  <div className="flex items-center justify-between mb-4 text-gray-900 dark:text-white">
    <button onClick={onPrevMonth} className="p-2 hover:bg-gray-900 rounded-full transition-colors">
      <ChevronLeft className="w-6 h-6" />
    </button>
    <h2 className="text-xl font-bold">
      {currentDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}
    </h2>
    <button onClick={onNextMonth} className="p-2 hover:bg-gray-900 rounded-full transition-colors">
      <ChevronRight className="w-6 h-6" />
    </button>
  </div>
);

// @ts-ignore
const ShareButtons = ({
  onShare,
  onShareToInstagram
}: {
  onShare: () => void;
  onShareToInstagram: () => void;
}) => (
  <div className="flex gap-4 justify-center mt-6">
    <button
      onClick={onShare}
      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
    >
      <Share2 className="w-5 h-5" />
      Compartilhar
    </button>
    <button
      onClick={onShareToInstagram}
      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full hover:opacity-90 transition-opacity"
    >
      <Instagram className="w-5 h-5" />
      Story
    </button>
  </div>
);

// Modificar o array de dias da semana para incluir identificadores únicos
const WEEK_DAYS = [
  { id: 'dom', label: 'D' },
  { id: 'seg', label: 'S' },
  { id: 'ter', label: 'T' },
  { id: 'qua', label: 'Q' },
  { id: 'qui', label: 'Q' },
  { id: 'sex', label: 'S' },
  { id: 'sab', label: 'S' }
];

function useProgressTimer({ duration, interval = 100, onComplete }: {
  duration: number;
  interval?: number;
  onComplete?: () => void;
}) {
  const [progress, setProgress] = useState(duration);
  const timerRef = useRef(0);
  const timerState = useRef({
    startTime: 0,
    remaining: duration,
    isPaused: false,
  });

  const cleanup = useCallback(() => {
    window.clearInterval(timerRef.current);
  }, []);

  const reset = useCallback(() => {
    cleanup();
    setProgress(duration);
    timerState.current = {
      startTime: 0,
      remaining: duration,
      isPaused: false,
    };
  }, [duration, cleanup]);

  const start = useCallback(() => {
    const state = timerState.current;
    state.startTime = Date.now();
    state.isPaused = false;

    timerRef.current = window.setInterval(() => {
      const elapsedTime = Date.now() - state.startTime;
      const remaining = Math.max(0, state.remaining - elapsedTime);

      setProgress(remaining);

      if (remaining <= 0) {
        cleanup();
        onComplete?.();
      }
    }, interval);
  }, [interval, cleanup, onComplete]);

  const pause = useCallback(() => {
    const state = timerState.current;
    if (!state.isPaused) {
      cleanup();
      state.remaining = Math.max(0, state.remaining - (Date.now() - state.startTime));
      state.isPaused = true;
    }
  }, [cleanup]);

  const resume = useCallback(() => {
    const state = timerState.current;
    if (state.isPaused && state.remaining > 0) {
      start();
    }
  }, [start]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    progress,
    start,
    pause,
    resume,
    reset,
  };
}

// Adicione essas frases motivacionais
const MOTIVATIONAL_PHRASES = {
  streak1: ["Excelente começo!", "Primeiro dia concluído!", "O início de uma jornada incrível!"],
  streak2: ["Dois dias seguidos! Continue assim!", "Você está pegando o ritmo!"],
  streak3: ["Três dias! Sua consistência é impressionante!", "Você já formou um hábito!"],
  streak5: ["Cinco dias! Você está imparável!", "Que dedicação incrível!"],
  streak7: ["Uma semana completa! Sensacional!", "Sete dias de conhecimento acumulado!"],
  streak10: ["Dez dias! Você é excepcional!", "Que força de vontade impressionante!"],
  streak14: ["Duas semanas! Você está entre nossos leitores mais fiéis!", "Quinze dias de consistência!"],
  streak21: ["Três semanas! Você é uma inspiração!", "21 dias - um hábito consolidado!"],
  streak30: ["Um mês completo! Inacreditável!", "30 dias de jornada! Você é extraordinário!"],
  streakLong: ["Sua dedicação é admirável!", "Impressionante constância!", "Você é o tipo de leitor que amamos!"]
};

// Função para obter frase motivacional baseada no streak
const getMotivationalPhrase = (streakCount: number): string => {
  if (streakCount <= 0) return "Comece sua jornada hoje!";
  if (streakCount === 1) return MOTIVATIONAL_PHRASES.streak1[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.streak1.length)];
  if (streakCount === 2) return MOTIVATIONAL_PHRASES.streak2[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.streak2.length)];
  if (streakCount === 3) return MOTIVATIONAL_PHRASES.streak3[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.streak3.length)];
  if (streakCount <= 5) return MOTIVATIONAL_PHRASES.streak5[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.streak5.length)];
  if (streakCount <= 7) return MOTIVATIONAL_PHRASES.streak7[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.streak7.length)];
  if (streakCount <= 10) return MOTIVATIONAL_PHRASES.streak10[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.streak10.length)];
  if (streakCount <= 14) return MOTIVATIONAL_PHRASES.streak14[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.streak14.length)];
  if (streakCount <= 21) return MOTIVATIONAL_PHRASES.streak21[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.streak21.length)];
  if (streakCount <= 30) return MOTIVATIONAL_PHRASES.streak30[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.streak30.length)];
  return MOTIVATIONAL_PHRASES.streakLong[Math.floor(Math.random() * MOTIVATIONAL_PHRASES.streakLong.length)];
};

// Melhore a função de reporte de erro
async function reportError(error: Error, code: ErrorCode, userId?: string) {
  try {
    const errorDetails = {
      message: error.message,
      code: code,
      details: error.stack,
      timestamp: new Date().toISOString(),
      userId: userId || 'anonymous',
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language
    };

    const response = await fetch(`${backendUrl}/streaks/error`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(errorDetails),
    });

    if (!response.ok) {
      console.error('Erro ao reportar erro:', await response.text());
      return null;
    }

    const data = await response.json();
    return data.data.errorId;
  } catch (reportError) {
    console.error('Erro ao reportar erro:', reportError);
    return null;
  }
}

export function StreaksPage() {
  const [_rankingData, setRankingData] = useState<RankingData | null>(null);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const [readingDates, setReadingDates] = useState<ReadingDate[]>([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<'streak' | 'graph'>('streak');
  const pageRef = useRef(null);
  const [error, setError] = useState<string | null>(null);
  const { user, login } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null);

  // Adicionar efeito para mostrar confetti quando mudar para view graph
  useEffect(() => {
    if (view === 'graph') {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [view]);

  const handleError = async (error: Error, code: ErrorCode) => {
    const storedReaderInfo = localStorage.getItem('reader_info');
    const readerInfo = storedReaderInfo ? JSON.parse(storedReaderInfo) : null;
    
    // Adicione informações de contexto ao erro
    const errorWithContext = {
      ...error,
      context: {
        url: window.location.href,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        platform: navigator.platform
      }
    };

    const errorId = await reportError(errorWithContext, code, readerInfo?.email);
    
    setErrorDetails({
      code,
      message: ERROR_MESSAGES[code] || ERROR_MESSAGES.UNKNOWN,
      errorId,
      details: error.stack
    });
    setError('error');
  };


  useEffect(() => {

    const storedReaderInfo = localStorage.getItem('reader_info');
    let readerInfo: ReaderInfo | null = storedReaderInfo ? JSON.parse(storedReaderInfo) : null;

    const email = searchParams.get('email') ? searchParams.get('email') : readerInfo?.email;
    
    if (!email) {
      throw new Error('E-mail não fornecido');
    }

    const fetchRanking = async () => {
      try {
        const response = await fetch(`${backendUrl}/streaks/ranking?email=${email}`);
        if (!response.ok) {
          throw new Error('Falha ao carregar o ranking');
        }
        const data = await response.json();
        setRankingData(data.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar o ranking');
      } finally {
        setLoading(false);
      }
    };

    fetchRanking();
  }, [searchParams]);

  const fetchReadingDates = useCallback(async () => {
    let retryCount = 0;
    
    while (retryCount < MAX_RETRIES) {
      try {
        const storedReaderInfo = localStorage.getItem('reader_info');
        let readerInfo: ReaderInfo | null = storedReaderInfo ? JSON.parse(storedReaderInfo) : null;

        const email = searchParams.get('email') ? searchParams.get('email') : readerInfo?.email;
        
        if (!email) {
          throw new Error('E-mail não fornecido');
        }

        const response = await fetch(`${backendUrl}/streaks/dates?email=${email}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Usuário não encontrado');
          }
          if (response.status === 503 && retryCount < MAX_RETRIES) {
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * retryCount));
            continue;
          }
          throw new Error(`Erro ao buscar datas: ${response.statusText}`);
        }

        const data = await response.json();
        setReadingDates(data.data.readingDates);
        return;
      } catch (error) {
        if (error instanceof Error) {
          if (error.message.includes('E-mail não fornecido')) {
            await handleError(error, ErrorCode.INVALID_EMAIL);
          } else if (error.message.includes('Usuário não encontrado')) {
            await handleError(error, ErrorCode.NOT_FOUND);
          } else if (retryCount < MAX_RETRIES) {
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * retryCount));
            continue;
          } else {
            await handleError(error, ErrorCode.DATABASE_ERROR);
          }
        }
      }
    }
  }, [searchParams]);

  const getLocationAndSubmit = useCallback(async () => {
    try {
      const { email, postId } = getUserInfo(searchParams);

      if (!email || !postId) {
        setErrorDetails({
          code: ErrorCode.INVALID_EMAIL,
          message: 'E-mail ou ID do post não fornecidos'
        });
        setError('error');
        return;
      }

      const result: any = await submitToApi(email, postId, searchParams);

      if (!result.success) {
        setErrorDetails(result.error);
        setError('error');
        return;
      }

      setStreakData(result.data);
      await fetchReadingDates();

      // Redireciona somente após sucesso
      window.history.replaceState({}, '', '/streaks');

      // Exibe confetti se necessário
      if (result.data.currentStreak > 0 && result.data.currentStreak % 3 === 0) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
    } catch (error) {
      setErrorDetails({
        code: ErrorCode.UNKNOWN,
        message: 'Ocorreu um erro inesperado'
      });
      setError('error');
    } finally {
      setLoading(false);
    }
  }, [searchParams, fetchReadingDates]);

  useEffect(() => {
    getLocationAndSubmit();
  }, [getLocationAndSubmit]);

  // Adicione esta função para verificar inscrição e gerar link
  const checkSubscriptionAndGetLink = useCallback(async () => {
    try {
      const storedReaderInfo = localStorage.getItem('reader_info');
      const readerInfo: ReaderInfo | null = storedReaderInfo ? JSON.parse(storedReaderInfo) : null;
      const email = searchParams.get('email') ? searchParams.get('email') : readerInfo?.email;

      if (!email) {
        setIsSubscribed(false);
        return;
      }

      if (!user) {
        // Tenta fazer login com o email do streak
        await login(email, false);
      }

      if (user?.referrer_id) {
        setIsSubscribed(true);
        setReferralLink(`https://app.thenewscc.com.br/subscribe?ref=${user.referrer_id}`);
      } else {
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('Erro ao verificar inscrição:', error);
      setIsSubscribed(false);
    }
  }, [user, login, searchParams]);

  useEffect(() => {
    checkSubscriptionAndGetLink();
  }, [checkSubscriptionAndGetLink]);

  if (error) {
    return <ErrorPage error={errorDetails || {
      code: ErrorCode.UNKNOWN,
      message: 'Erro desconhecido',
    }} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  // Novos métodos
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Meu Streak no the news!',
          text: `Mantenho minha streak de ${streakData?.currentStreak} dias no the news! 🔥`,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(
          `Mantenho minha streak de ${streakData?.currentStreak} dias no the news! 🔥 ${window.location.href}`
        );
        alert('Link copiado para a área de transferência!');
      }
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
    }
  };

  const handleShareToInstagram = async () => {
    try {
      // Pré-carrega a imagem para garantir que esteja disponível
      const preloadImage = async (url: string): Promise<string> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          };
          img.onerror = reject;
          img.src = url;
        });
      };

      // Pré-carrega a logo
      const logoDataUrl = await preloadImage('https://app.thenewscc.com.br/streak_v3.png');

      const storyElement = document.createElement('div');
      storyElement.style.width = '1080px';
      storyElement.style.height = '1920px';
      storyElement.style.position = 'fixed';
      storyElement.style.left = '-9999px';
      storyElement.innerHTML = `<div style="width: 1080px; height: 1920px; position: relative;">
  <img 
    src=${logoDataUrl}
    style="width: 100%; height: 100%; object-fit: cover;"
  />
  
  <!-- Cards Container -->
  <div style="
    position: absolute;
    top: 55%;
    left: 76%; /* Adjusted left position */
    transform: translate(-50%, -50%);
    display: grid;
    grid-template-columns: repeat(2, 1fr); /* Ensures equal width columns */
    gap: 20px;
    width: 80%; /* Increased width */
    max-width: 100%; /* Increased max-width */
    padding: 0 20px ;
  ">
    <!-- Dias Seguidos Card -->
    <div style="
      border-radius: 16px;
      padding: 32px; /* Increased padding */
      transition: all 0.3s;
    ">
      <div style="
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px; /* Increased margin */
      "> <span style="font-size: 4.5rem; font-weight: bold; color: #FCD34D;">${streakData?.currentStreak}</span>
      </div>
    </div>

    <!-- Recorde Pessoal Card -->
    <div style="
      padding: 32px; /* Increased padding */
      transition: all 0.3s;
    ">
      <div style="
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px; /* Increased margin */
      ">
        <span style="font-size: 4.5rem; font-weight: bold; color: #FCD34D;">${streakData?.maxStreak}</span>
      </div>
    </div>
  </div>

  </div>
</div>
      `;

      document.body.appendChild(storyElement);

      const canvas = await html2canvas(storyElement, {
        width: 1080,
        height: 1920,
        scale: 1,
        allowTaint: true,
        useCORS: true,
        backgroundColor: null,
      });

      // Substitua o código de download por:
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob as Blob);
        }, 'image/png');
      });

      // Adiciona chamada à API após compartilhamento bem-sucedido
      try {
        await fetch(`${backendUrl}/streaks/share`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: readerInfo?.email,
            platform: 'instagram'
          }),
        });
      } catch (apiError) {
        console.error('Erro ao registrar compartilhamento:', apiError);
      }

      // Verifica se a API Web Share está disponível e suporta compartilhamento de arquivos
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], 'the-news-streak-story.png', { type: 'image/png' });

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'Minha streak no the news!',
          });
        } else {
          // Fallback para dispositivos que não suportam compartilhamento de arquivos
          const imageUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = imageUrl;
          link.download = 'the-news-streak-story.png';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(imageUrl);
          alert('Imagem salva! Agora você pode compartilhar no Instagram.');
        }
      }

      document.body.removeChild(storyElement);
    } catch (error) {
      console.error('Erro ao gerar imagem:', error);
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  // Modifica o método generateCalendar para usar currentDate e implementar o estilo Duolingo
  const generateCalendar = () => {
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const days = [];
    const firstDay = startDate.getDay();
    
    // Ajuste para considerar o fuso horário nas datas de leitura
    const currentMonthReadingDays = readingDates
      .map(rd => {
        const date = new Date(rd.date);
        // Ajusta para o fuso horário local
        date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
        return date;
      })
      .filter(d => d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear())
      .map(d => d.getDate())
      .sort((a, b) => a - b);
    
    // Identificar sequências consecutivas
    const sequences: number[][] = [];
    let currentSequence: number[] = [];
    
    currentMonthReadingDays.forEach((day, index) => {
      if (index === 0 || day === currentMonthReadingDays[index - 1] + 1) {
        currentSequence.push(day);
      } else {
        if (currentSequence.length > 0) {
          sequences.push([...currentSequence]);
        }
        currentSequence = [day];
      }
    });
    
    if (currentSequence.length > 0) {
      sequences.push(currentSequence);
    }
  
    // Dias vazios no início (preenchimento antes do primeiro dia do mês)
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-10 h-10" />);
    }
  
    // Preencher os dias do mês
    for (let date = 1; date <= endDate.getDate(); date++) {
      const currentDateInLoop = new Date(currentDate.getFullYear(), currentDate.getMonth(), date);
      const isReadingDay = currentMonthReadingDays.includes(date);
      const isToday = new Date().toDateString() === currentDateInLoop.toDateString();
      
      // Encontrar a sequência que contém este dia
      const sequence = sequences.find(seq => seq.includes(date));
      const isInSequence = sequence !== undefined;
      const sequenceIndex = isInSequence ? sequence.indexOf(date) : -1;
      const isFirstInSequence = sequenceIndex === 0;
      const isLastInSequence = isInSequence && sequenceIndex === sequence.length - 1;
      const isMiddleInSequence = isInSequence && !isFirstInSequence && !isLastInSequence;
    
      
      // Classes para conectores de sequência (estilo Duolingo)
      let sequenceClasses = '';
      if (isInSequence && sequence.length > 1) {
        if (isFirstInSequence) {
          sequenceClasses = 'rounded-l-full left-1/2 right-0 w-[50%]';
        } else if (isLastInSequence) {
          sequenceClasses = 'rounded-r-full right-1/2 left-0 w-[50%]';
        } else if (isMiddleInSequence) {
          sequenceClasses = 'left-0 right-0 w-full';
        }
      }
      
      days.push(
        <div
          key={date}
          className={`
            relative w-14 h-14 flex items-center justify-center lg:w-28 lg:h-18
            ${isToday ? 'z-20' : 'z-10'}
          `}
        >
          {/* Conector para dias consecutivos em uma sequência */}
          {isInSequence && sequence.length > 1 && (
            <div
              className={`
                absolute top-1/2 -translate-y-1/2 h-8 bg-[#FFD700]  -z-10
                ${sequenceClasses}
              `}
            />
          )}
  
          {/* Círculo do dia */}
          <div 
            className={`
              relative w-10 h-10 rounded-full flex items-center justify-center 
              transition-all duration-300 hover:scale-110
              ${isToday ? 'ring-2 ring-[#FFD700] ring-opacity-50' : ''}
              ${isReadingDay 
                ? 'bg-[#FFD700] text-gray-900 font-bold z-10' 
                : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}
            `}
          >
            <span className="text-base">{date}</span>
            
            {isToday && (
              <div className="absolute -top-1 -right-1 z-20">
                <div className="w-3 h-3 bg-white dark:bg-gray-900 rounded-full border-2 border-[#FFD700]" />
              </div>
            )}
          </div>
        </div>
      );
    }
  
    return days;
  };

  const getStreakEmoji = () => {
    if (!streakData) return '🌱';
    if (streakData.currentStreak <= 3) return '🌱';
    if (streakData.currentStreak <= 7) return '🔥';
    return '⚡';
  };

  const storedReaderInfo = localStorage.getItem('reader_info');
  const readerInfo: ReaderInfo | null = storedReaderInfo ? JSON.parse(storedReaderInfo) : null;

  // Substitua o botão existente por este componente
  const ActionButton = () => {
    const [open, setOpen] = useState(false);
    const toastDuration = 5000;
    const { progress, start, pause, resume, reset } = useProgressTimer({
      duration: toastDuration,
      onComplete: () => setOpen(false),
    });

    const handleOpenChange = useCallback(
      (isOpen: boolean) => {
        setOpen(isOpen);
        if (isOpen) {
          reset();
          start();
        }
      },
      [reset, start],
    );

    const handleCopyLink = useCallback(async () => {
      if (open) {
        setOpen(false);
        window.setTimeout(() => {
          handleOpenChange(true);
        }, 150);
      } else {
        await navigator.clipboard.writeText(referralLink);
        handleOpenChange(true);
      }
    }, [open, handleOpenChange, referralLink]);

    if (isSubscribed && referralLink) {
      return (
        <>
          <ToastProvider swipeDirection="left">
            <div className="flex flex-col items-center space-y-4 mb-12">
              <div className="relative group w-full">
                
                <div className="absolute bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-lg opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                <div className="flex flex-col items-center space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2 mb-4 ">
                  <span className="text-yellow-500 font-bold">•</span>
                  <span className="animate-pulse font-medium">Ganhe recompensas exclusivas!</span>
                </div>
                </div>
                <button
                  onClick={handleCopyLink}
                  className="relative w-full py-3 px-4 bg-[#FFCE04] dark:from-[#ffd000] dark:via-[#ffdb4d] dark:to-[#ffe680] text-gray-900 rounded-lg font-medium text-base transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 ease-out shadow-md shadow-yellow-500/20 flex items-center justify-center gap-2 group"
                >
                  <span className="relative flex items-center gap-2">
                    <svg className="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                    </svg>
                    Convide amigos
                    <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
                  </span>
                </button>
              </div>
              <div className="flex flex-col items-center space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-2">
                  <svg 
                    className="w-4 h-4" 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11a1 1 0 10-2 0V9.414L7.707 10.707a1 1 0 01-1.414-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 9.414V13z"
                      clipRule="evenodd" 
                    />
                  </svg>
                  <span>Clique para copiar seu link único</span>
                </span>
                
              </div>
            </div>

            <Toast 
              open={open} 
              onOpenChange={handleOpenChange} 
              onPause={pause} 
              onResume={resume}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-lg rounded-xl p-4"
            >
              <div className="flex w-full justify-between gap-4">
                <div className="size-8 rounded-full bg-[#FFCE04]/10 flex items-center justify-center shrink-0">
                  <CircleCheckIcon
                    className="text-[#FFCE04]"
                    size={20}
                    aria-hidden="true"
                  />
                </div>
                <div className="flex grow flex-col gap-2">
                  <div className="space-y-1">
                    <ToastTitle className="text-base font-semibold text-gray-900 dark:text-gray-100">
                      Link copiado com sucesso!
                    </ToastTitle>
                    <ToastDescription className="text-sm text-gray-600 dark:text-gray-400">
                      Compartilhe com seus amigos e ganhe recompensas exclusivas.
                    </ToastDescription>
                  </div>
                  <div className="pt-1">
                    <ToastAction altText="Acompanhar progresso" asChild>
                      <Button 
                        size="sm" 
                        onClick={() => window.location.href = '/'}
                        className="bg-[#FFCE04] text-gray-900 hover:bg-[#FFD84D] transition-all duration-300 
                          transform hover:scale-[1.02] active:scale-[0.98] font-medium rounded-lg
                          shadow-md shadow-yellow-500/20 hover:shadow-yellow-500/30"
                      >
                        Acompanhar progresso
                      </Button>
                    </ToastAction>
                  </div>
                </div>
                <ToastClose asChild>
                  <Button
                    variant="ghost"
                    className="group -my-1.5 -me-2 size-8 shrink-0 p-0 hover:bg-gray-100 
                      dark:hover:bg-gray-800 rounded-full transition-colors"
                    aria-label="Fechar notificação"
                  >
                    <XIcon
                      size={16}
                      className="text-gray-500 dark:text-gray-400 transition-opacity group-hover:opacity-100"
                      aria-hidden="true"
                    />
                  </Button>
                </ToastClose>
              </div>
              <div className="contents" aria-hidden="true">
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#FFCE04] via-[#FFD84D] to-[#FFCE04] 
                      animate-gradient-x"
                    style={{
                      width: `${(progress / toastDuration) * 100}%`,
                      transition: "width 100ms linear",
                    }}
                  />
                </div>
              </div>
            </Toast>
            <ToastViewport className="sm:right-auto sm:left-0" />
          </ToastProvider>
        </>
      );
    }

    return (
      <button
        onClick={() => window.open(streakData?.url || 'https://thenewscc.beehiiv.com', '_blank')}
        className="w-full py-4 bg-[#615A5A] bg-gradient-to-r from-yellow-400 to-yellow-500 
          dark:from-[#ffd000] dark:to-[#ffd000] text-gray-900 rounded-xl font-bold text-lg
          hover:from-yellow-500 hover:to-yellow-600 dark:hover:from-[#ffd000] dark:hover:to-[#ffd000]
          transform hover:scale-[1.02] transition-all duration-300 mb-12 shadow-lg shadow-yellow-500/20"
      >
        Continuar Lendo
      </button>
    );
  };

  return (
    <ThemeProvider defaultTheme="dark" storageKey="ui-theme">
      <ToastProvider>
        <div ref={pageRef} className="min-h-screen bg-gray-50 dark:bg-gray-900">
          {errorDetails && (
            <Toast open={!!error} onOpenChange={() => setError(null)}>
              <ToastTitle>{errorDetails.message}</ToastTitle>
              {errorDetails.details && (
                <ToastDescription>{errorDetails.details}</ToastDescription>
              )}
              <ToastClose />
            </Toast>
          )}
          
          {showConfetti && (
            <Confetti
              width={window.innerWidth}
              height={window.innerHeight}
              recycle={false}
              numberOfPieces={200}
              colors={['#FFD700', '#FFA500', '#FF6347']}
              gravity={0.3}
            />
          )}

          <div className="max-w-6xl mx-auto px-4 py-8 lg:py-12">
            <div className="flex justify-center items-center p-4 dark:bg-transparent">
              <div className="flex justify-center items-center">
                <img
                  src="https://app.thenewscc.com.br/thenews.webp"
                  alt="Logo"
                  className="w-40 rounded-lg"
                />
              </div>
              <ThemeToggle />
            </div>

            {view === 'streak' ? (
              <div className="text-center mb-6">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-[#ffd000]">
                  Sua Jornada de Leitura
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">Acompanhe seu progresso diário</p>
              </div>
            ) : view === 'graph' ? (
              <div className="text-center mb-6">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-[#ffd000]">
               the point leaders
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">mais inteligentes (e pontuados) em 5 minutos</p>
              </div>
            ) : (
              <div className="text-center mb-6">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-[#ffd000]">
                  Ranking
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">Confira sua posição</p>
              </div>
            )}

            {/* View Toggle Buttons */}
            <div className="flex justify-center gap-2 mb-8">
              <button
                onClick={() => setView('streak')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  view === 'streak'
                    ? 'bg-[#ffd000] text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                Streak
              </button>
              <button
                onClick={() => setView('graph')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  view === 'graph'
                    ? 'bg-[#ffd000] text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                Point Leaders
              </button>
              {/* <button
                onClick={() => setView('rank')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  view === 'rank'
                    ? 'bg-[#ffd000] text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                Ranking
              </button> */}
            </div>

            {view === 'streak' && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                <div className="lg:col-span-4 space-y-4">
                  <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
                      {/* Streak Atual */}
                      <div className="flex flex-col items-center justify-center px-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{getStreakEmoji()}</span>
                          <span className="text-2xl font-bold text-yellow-400">{streakData?.currentStreak || 0}</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Dias Seguidos</p>
                      </div>

                      {/* Recorde */}
                      <div className="flex flex-col items-center justify-center px-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">🏆</span>
                          <span className="text-2xl font-bold text-yellow-400">{streakData?.maxStreak || 0}</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Recorde</p>
                      </div>

                    </div>
                  </div>


                  <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 lg:p-5 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Progresso da Semana</h3>
                    <div className="grid grid-cols-7 gap-2">
                      {WEEK_DAYS.map((day) => (
                        <div key={`progress-${day.id}`} className="text-center">
                          <div className="text-sm text-gray-400 mb-2">{day.label}</div>
                          <div
                            className={`
                              aspect-square rounded-xl transition-all duration-300
                              ${streakData?.weekdays?.[['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'][WEEK_DAYS.indexOf(day)] as keyof typeof streakData.weekdays]
                                ? 'bg-gradient-to-br from-[#ffd000] to-[#ffd000] shadow-lg shadow-yellow-500/20'
                                : 'bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600'
                              }
                            `}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="hidden lg:block">
                    <div className="flex flex-col gap-4">
                      <ActionButton />
                      <div className="flex flex-col gap-4 justify-center">
                        <button
                          onClick={handleShare}
                          className="flex items-center justify-center gap-3 px-6 py-3 text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800/80 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700/80 transition-all duration-300"
                        >
                          <Share2 className="w-5 h-5" />
                          <span className="font-medium">Compartilhar</span>
                        </button>
                        <button
                          onClick={handleShareToInstagram}
                          className="flex items-center justify-center gap-3 px-6 py-3 text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800/80 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700/80 transition-all duration-300"
                        >
                          <Instagram className="w-5 h-5" />
                          <span className="font-medium">Compartilhar no Story</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-8 mb-12">
                  <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 lg:p-5 border border-gray-200 dark:border-gray-700 shadow-xl shadow-yellow-500/5">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-[#ffd000]" />
                        Histórico de Leituras
                      </h3>
                      
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#ffd000]"></div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Dias lidos</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-6 h-2.5 rounded-full bg-[#ffd000] opacity-80"></div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Sequência</span>
                        </div>
                      </div>
                    </div>

                    <MonthNavigation
                      currentDate={currentDate}
                      onPrevMonth={handlePrevMonth}
                      onNextMonth={handleNextMonth}
                    />

                    <div className="grid grid-cols-7 gap-1.5 mb-3">
                      {WEEK_DAYS.map((day) => (
                        <div key={`calendar-${day.id}`} className="text-center text-xs font-medium text-gray-900 dark:text-[#ffd000]">
                          {day.label}
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1.5">
                      {generateCalendar()}
                    </div>
                    
                    <div className="mt-8 pt-5 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex flex-col items-center gap-3 text-center">
                        <div className="flex items-center">
                          {Array.from({ length: Math.min(7, Math.max(3, streakData?.currentStreak || 0)) }).map((_, i) => (
                            <div 
                              key={i} 
                              className={`
                                w-8 h-8 rounded-full bg-[#ffd000] -ml-2 first:ml-0
                                flex items-center justify-center shadow-md
                                ${i === 0 ? 'z-50' : `z-${50-i*10}`}
                                ${i === 0 ? '' : ''}
                              `}
                              style={{ zIndex: 50-i }}
                            >
                              <img 
                                src="/full_streak.png" 
                                alt="streak" 
                                className="w-5 h-5 transform -translate-y-0.5"
                              />
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-1 text-gray-600 dark:text-gray-300 text-base font-medium">
                          {getMotivationalPhrase(streakData?.currentStreak || 0)}
                        </div>
                        
                        <div className="text-xs text-gray-500 dark:text-gray-400 italic mt-1">
                          the news. você mais inteligente em 5 minutos. <span className="not-italic">✨</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {view === 'graph' && (
              <div className="bg-transparent">
                <PointLeadersGraph email={readerInfo?.email || ''} />
              </div>
            )}

            {/* {view === 'rank' && (
              <RankingPage email={readerInfo?.email || ''} />
            )} */}

            <Footer />
          </div>
        </div>
      </ToastProvider>
    </ThemeProvider>
  );
}